import { i18n } from "@dehub/theme";
import {
  Box,
  Button,
  FileButton,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { yupResolver } from "mantine-form-yup-resolver";
import dayjs from "dayjs";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";
import { MediaApi } from "../../apis";
import { IAuth } from "../../interfaces/IAuth";
import { message } from "../../utils/Message";
import { renderName } from "../../utils/render-name";
import { AppModalHeader, ModalFooter } from "../ui/app-modal";
import classes from "./invoice-attachment-modal.module.css";

type IUser = {
  avatar: string | undefined;
  firstName: string | undefined;
  id?: string | undefined;
  lastName: string | undefined;
};

type FormValues = {
  name: string;
  description: string;
  url: string;
  date: string | undefined;
  attachedUser: IUser | undefined;
  id: string;
  createdAt: Date | undefined;
};

const schema = yup.object({
  name: yup.string().required(() => i18n.t("common:mustFill")),
  description: yup.string().required(() => i18n.t("common:mustFill")),
  url: yup.string().required(() => i18n.t("common:mustFill")),
});

/** Survives React StrictMode remount so one drop → one MediaApi call. */
const startedUploadIds = new Set<string>();

const UUID_NAME_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-/i;

function fileExt(name?: string) {
  const m = /\.([a-z0-9]+)(?:\?|$)/i.exec(name || "");
  return (m?.[1] || "FILE").toUpperCase().slice(0, 4);
}

function nameWithoutExt(filename: string) {
  return filename.replace(/\.[^.]+$/, "");
}

/** UUID-like basenames must not auto-fill the Name field. */
function suggestNameFromFile(filename: string) {
  const base = nameWithoutExt(filename);
  return UUID_NAME_RE.test(base) ? "" : base;
}

/** Middle ellipsis for long / UUID filenames in the file card. */
function middleEllipsis(name: string, max = 36) {
  if (!name || name.length <= max) return name;
  const keep = Math.max(8, Math.floor((max - 1) / 2));
  return `${name.slice(0, keep)}…${name.slice(-keep)}`;
}

function formatBytes(n?: number) {
  if (n == null || !Number.isFinite(n)) return "-";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatAttachedAt(date?: string | Date) {
  return dayjs(date ?? new Date()).format("YYYY-MM-DD HH:mm");
}

type PendingPayload = {
  pendingFile?: File;
  /** Unique per drop/open — guards StrictMode double-invoke. */
  uploadId?: string;
  name?: string;
  description?: string;
  url?: string;
  id?: string;
  createdAt?: Date;
  attachedUser?: IUser;
  date?: string;
};

type UploadPhase = "idle" | "uploading" | "done" | "error";

export function InvoiceAttachmentModalForm({
  action,
  setAction,
  values: propsValues,
  setFieldValue: setFieldValueProps,
}: any) {
  const { t } = useTranslation("invoice");
  const { user } = useSelector((state: { auth: IAuth }) => state.auth);
  const now = new Date();
  const mode = action[0] as "add" | "edit" | "details";
  const isDetails = mode === "details";
  const isEdit = mode === "edit";
  const payload = (action[1] || {}) as PendingPayload;

  const [localFile, setLocalFile] = useState<File | null>(payload.pendingFile ?? null);
  const [phase, setPhase] = useState<UploadPhase>(
    payload.pendingFile ? "uploading" : payload.url ? "done" : "idle",
  );
  const [progress, setProgress] = useState(payload.pendingFile ? 12 : payload.url ? 100 : 0);
  const uploadToken = useRef(0);
  const lastBlobRef = useRef<File | null>(payload.pendingFile ?? null);

  const form = useForm<FormValues>({
    mode: "controlled",
    initialValues: {
      name:
        payload.name ||
        (payload.pendingFile ? suggestNameFromFile(payload.pendingFile.name) : ""),
      description: payload.description || "",
      url: payload.url || "",
      date: payload.date,
      attachedUser: payload.attachedUser || {
        lastName: user?.lastName,
        firstName: user?.firstName,
        avatar: user?.avatar ?? undefined,
      },
      id: payload.id || "",
      createdAt: payload.createdAt,
    },
    validate: yupResolver(schema),
  });

  const uploadFile = async (blob: File, opts?: { force?: boolean; uploadId?: string }) => {
    const uploadId = opts?.uploadId;
    if (uploadId && !opts?.force) {
      if (startedUploadIds.has(uploadId)) return;
      startedUploadIds.add(uploadId);
    }

    const token = ++uploadToken.current;
    lastBlobRef.current = blob;
    setLocalFile(blob);
    setPhase("uploading");
    setProgress(12);
    // Only auto-fill when Name is empty; UUID-like basenames stay blank.
    if (!form.values.name) {
      form.setFieldValue("name", suggestNameFromFile(blob.name));
    }
    form.setFieldValue("url", "");

    try {
      const body = new FormData();
      body.append("file", blob);
      setProgress(45);
      const res = await MediaApi.uploadFile(body);
      if (token !== uploadToken.current) return;
      form.setFieldValue("url", res.url);
      setProgress(100);
      setPhase("done");
    } catch (err: any) {
      if (token !== uploadToken.current) return;
      message.error(err?.message || t("common:actionFailed"));
      setProgress(100);
      setPhase("error");
      form.setFieldValue("url", "");
      if (uploadId) startedUploadIds.delete(uploadId);
    }
  };

  // Start pending-file upload once per uploadId (not per StrictMode effect cycle).
  useEffect(() => {
    if (isDetails) return;
    if (!payload.pendingFile || !payload.uploadId) return;
    void uploadFile(payload.pendingFile, { uploadId: payload.uploadId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload.uploadId]);

  const onSubmit = (values: FormValues) => {
    if (mode === "add") {
      setFieldValueProps("attachments", [
        ...(propsValues.attachments || []),
        { ...values, id: uuidv4(), createdAt: new Date() },
      ]);
    } else if (mode === "edit") {
      setFieldValueProps("attachments", [
        ...propsValues.attachments.filter((f: any) => f.id !== values.id),
        { ...values, createdAt: values.createdAt || new Date() },
      ]);
    }
    setAction([]);
  };

  const title =
    isDetails
      ? t("itemForm.attachTitle")
      : isEdit
        ? t("itemForm.attachEditTitle")
        : t("itemForm.attachAddTitle");
  const hint =
    isDetails
      ? t("itemForm.attachDetailsHint")
      : isEdit
        ? t("itemForm.attachEditHint")
        : t("itemForm.attachAddHint");

  const rawFileName = localFile?.name || form.values.name || "-";
  const displayName = middleEllipsis(rawFileName);
  const sizeLabel = localFile ? formatBytes(localFile.size) : undefined;
  const hasFile = Boolean(localFile || form.values.url || phase === "error");
  const uploading = phase === "uploading";
  const canAttach = !uploading && !!form.values.url && !isDetails;
  const suggestedFromFile = localFile ? suggestNameFromFile(localFile.name) : "";
  const nameAutoFilled = Boolean(suggestedFromFile && form.values.name === suggestedFromFile);

  let statusNode: ReactNode = null;
  if (phase === "uploading") {
    statusNode = <Text className={classes.fileStatus}>{t("itemForm.uploading")}</Text>;
  } else if (phase === "done") {
    statusNode = (
      <Text className={classes.fileStatus}>
        {sizeLabel ? `${sizeLabel} · ${t("itemForm.uploaded")}` : t("itemForm.fileReady")}
      </Text>
    );
  } else if (phase === "error") {
    statusNode = (
      <Text
        className={`${classes.fileStatus} ${classes.fileStatusError}`}
        onClick={() => {
          const blob = lastBlobRef.current;
          if (blob) void uploadFile(blob, { force: true, uploadId: payload.uploadId || uuidv4() });
        }}
      >
        {t("itemForm.retryUpload")}
      </Text>
    );
  }

  return (
    <Box className={classes.root}>
      <AppModalHeader title={title} subtitle={hint} onClose={() => setAction([])} />
      <Box className={classes.body}>
        {hasFile ? (
          <Box className={classes.fileCard}>
            <Box component="span" className={classes.typeBadge}>
              {fileExt(localFile?.name || form.values.name || form.values.url)}
            </Box>
            <Box className={classes.fileMeta}>
              <Box className={classes.fileTop}>
                <Text className={classes.fileName} title={rawFileName}>
                  {displayName}
                </Text>
                {statusNode}
              </Box>
              <Box className={classes.progressTrack}>
                <Box
                  className={classes.progressFill}
                  data-done={phase === "done" || undefined}
                  data-error={phase === "error" || undefined}
                  style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                />
              </Box>
            </Box>
            {!isDetails ? (
              <FileButton
                onChange={(f) => {
                  if (f) void uploadFile(f, { force: true, uploadId: uuidv4() });
                }}
                accept="application/pdf,image/jpeg,image/png,image/webp,.xlsx,.xls"
              >
                {(props) => (
                  <Button
                    {...props}
                    className={classes.replaceBtn}
                    variant="default"
                    size="xs"
                    disabled={uploading}
                  >
                    {t("itemForm.replaceFile")}
                  </Button>
                )}
              </FileButton>
            ) : null}
          </Box>
        ) : !isDetails ? (
          <FileButton
            onChange={(f) => {
              if (f) void uploadFile(f, { force: true, uploadId: uuidv4() });
            }}
            accept="application/pdf,image/jpeg,image/png,image/webp,.xlsx,.xls"
          >
            {(props) => (
              <Box className={classes.emptyFile} {...props}>
                {t("itemForm.pickFileHint")}
              </Box>
            )}
          </FileButton>
        ) : (
          <Box className={classes.emptyFile} style={{ cursor: "default" }}>
            {t("itemForm.noFile")}
          </Box>
        )}

        <Box>
          <TextInput
            className={classes.field}
            label={t("itemForm.name")}
            placeholder={t("itemForm.namePlaceholder")}
            required
            disabled={isDetails}
            data-autofocus
            {...form.getInputProps("name")}
          />
          {nameAutoFilled ? (
            <Text className={classes.fieldHint}>{t("itemForm.nameAutoFilledHint")}</Text>
          ) : null}
        </Box>

        <Textarea
          className={`${classes.field} ${classes.descInput}`}
          label={t("itemForm.description")}
          placeholder={t("itemForm.descriptionPlaceholder")}
          required
          minRows={3}
          autosize={false}
          disabled={isDetails}
          {...form.getInputProps("description")}
        />

        <Box className={classes.infoRow}>
          <Text span className={classes.infoItem}>
            <Text span className={classes.infoLabel}>
              {t("itemForm.attachedBy")}
            </Text>
            <Text span className={classes.infoValue}>
              {renderName(form.values.attachedUser)}
            </Text>
          </Text>
          <Text span className={classes.infoItem}>
            <Text span className={classes.infoLabel}>
              {t("itemForm.attachedDate")}
            </Text>
            <Text span className={classes.infoDate}>
              {formatAttachedAt(form.values.createdAt || now)}
            </Text>
          </Text>
        </Box>

        {form.errors.url && phase !== "uploading" ? (
          <Text fz="sm" c="red.8">
            {String(form.errors.url)}
          </Text>
        ) : null}
      </Box>

      <ModalFooter>
        <Button variant="default" size="md" onClick={() => setAction([])}>
          {isDetails ? t("itemForm.done") : t("itemForm.cancel")}
        </Button>
        {!isDetails ? (
          <Button
            color="brand"
            size="md"
            disabled={!canAttach}
            onClick={() => {
              const result = form.validate();
              if (!result.hasErrors) onSubmit(form.values);
            }}
          >
            {t("itemForm.attach")}
          </Button>
        ) : null}
      </ModalFooter>
    </Box>
  );
}
